import {
  findLighthouseIssueEntry,
} from "./enrichAffectedResources";
import { orderIssuesForDisplay } from "./issueOrdering";

function flattenIssueItems(issue) {
  const out = [];
  const walk = (items) => {
    if (!Array.isArray(items)) return;
    for (const item of items) {
      if (!item || typeof item !== "object") continue;
      out.push(item);
      if (item.subItems?.items) walk(item.subItems.items);
      if (Array.isArray(item.items)) walk(item.items);
    }
  };
  walk(issue?.items);
  if (issue?.details?.items && Array.isArray(issue.details.items)) {
    walk(issue.details.items);
  }
  return out;
}

function getItemUrl(item) {
  return item?.url || item?.source?.url || null;
}

function looksLikeFixHint(title) {
  const t = (title || "").trim();
  if (!t) return false;
  if (/;\s*\w/.test(t)) return true;
  if (/:\s*(defer|use|load|split|move|add|remove|enable)/i.test(t)) return true;
  if (/next\/script|partytown|dynamic import/i.test(t)) return true;
  return t.length > 72;
}

function sanitizeAiTitle(title) {
  if (!title || !looksLikeFixHint(title)) return title;
  const beforeColon = title.split(":")[0]?.trim();
  if (beforeColon && beforeColon.length >= 8 && beforeColon.length <= 64) {
    return beforeColon;
  }
  return title.length > 80 ? `${title.slice(0, 77)}…` : title;
}

function normalizeCompareText(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function pickDisplayFinding(issue, lhIssue) {
  const candidates = [issue?.finding, lhIssue?.display].filter(Boolean);
  const titleKey = normalizeCompareText(lhIssue?.title || issue?.title);

  for (const candidate of candidates) {
    const key = normalizeCompareText(candidate);
    if (!key || key === titleKey) continue;
    if (titleKey && (key.startsWith(titleKey) || titleKey.startsWith(key))) continue;
    return candidate;
  }

  return "";
}

function findIssueByAffectedUrls(knownIssues, affected) {
  const needles = (affected || [])
    .map((text) => String(text).trim())
    .filter((text) => text.includes("http") || text.includes("/"));
  if (!needles.length) return null;

  for (const [key, issue] of Object.entries(knownIssues || {})) {
    const urls = flattenIssueItems(issue)
      .map(getItemUrl)
      .filter(Boolean);
    if (
      needles.some((needle) =>
        urls.some(
          (url) =>
            url === needle ||
            url.includes(needle) ||
            needle.includes(url) ||
            needle.includes(url.split("/").pop()),
        ),
      )
    ) {
      return { key, issue };
    }
  }
  return null;
}

function findIssueByKeywords(knownIssues, title) {
  const t = (title || "").toLowerCase();
  const keywordMap = [
    {
      test: /unused\s*(js|javascript)|unused js\/css/i,
      keys: ["unused-javascript", "unusedResources", "unused-css-rules"],
    },
    {
      test: /unused\s*css/i,
      keys: ["unused-css-rules", "unusedResources", "unused-javascript"],
    },
    {
      test: /render[- ]blocking/i,
      keys: ["renderBlockingInsight", "render-blocking-resources"],
    },
    {
      test: /third[- ]party/i,
      keys: ["thirdPartiesInsight", "third-party-summary"],
    },
    {
      test: /color contrast/i,
      keys: ["colorContrast"],
    },
    {
      test: /largest contentful paint|\blcp\b/i,
      keys: ["largest-contentful-paint", "lcp-lazy-loaded"],
    },
  ];

  for (const { test, keys } of keywordMap) {
    if (!test.test(t)) continue;
    for (const key of keys) {
      if (knownIssues?.[key]) return { key, issue: knownIssues[key] };
    }
  }
  return null;
}

/**
 * Map an AI suggestion block to the correct Lighthouse issue for display.
 */
export function resolveAiSuggestionIssue(issue, knownIssues, listIndex = 0) {
  const ordered = orderIssuesForDisplay(knownIssues);
  let entry = findLighthouseIssueEntry(knownIssues, issue?.title);

  if (!entry && issue?.issueNumber) {
    const byNumber = ordered.find((row) => row.n === issue.issueNumber);
    if (byNumber) entry = { key: byNumber.id, issue: byNumber.issue };
  }

  if (!entry && listIndex >= 0 && listIndex < ordered.length) {
    const titleMismatch =
      looksLikeFixHint(issue?.title) ||
      !findLighthouseIssueEntry(knownIssues, issue?.title);
    if (titleMismatch) {
      const byIndex = ordered[listIndex];
      if (byIndex) entry = { key: byIndex.id, issue: byIndex.issue };
    }
  }

  if (!entry && issue?.affected?.length) {
    entry = findIssueByAffectedUrls(knownIssues, issue.affected);
  }

  if (!entry && looksLikeFixHint(issue?.title)) {
    entry = findIssueByKeywords(knownIssues, issue.title);
  }

  const displayTitle = entry?.issue?.title || sanitizeAiTitle(issue?.title);
  const displayFinding = pickDisplayFinding(issue, entry?.issue);

  return {
    entry,
    displayTitle,
    displayFinding,
  };
}
